<template>
  <div class="text-button-element-wrapper">
    <!-- Modale de confirmation -->
    <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="cancelDelete">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Confirmer la suppression</h3>
          <button class="modal-close" @click="cancelDelete">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>Êtes-vous sûr de vouloir supprimer ce bouton ?</p>
          <div class="button-to-delete">
            <span class="button-title">{{ buttonToDelete?.title }}</span>
            <span class="button-block">{{ getBlockName(buttonToDelete?.id) }}</span>
          </div>
        </div>
        <div class="modal-footer">
          <button class="modal-cancel" @click="cancelDelete">
            <i class="fas fa-times"></i>
            Annuler
          </button>
          <button class="modal-confirm" @click="confirmDelete">
            <i class="fas fa-trash"></i>
            Supprimer
          </button>
        </div>
      </div>
    </div>

    <!-- En-tête du composant -->
    <div class="element-header">
      <span class="element-type-badge">
        <i class="fas fa-mouse-pointer"></i>
        <span>Message + Boutons</span>
      </span>
      <div class="element-actions">
        <button class="action-btn" @click.stop="$emit('duplicate')" title="Dupliquer">
          <i class="fas fa-copy"></i>
        </button>
        <button class="action-btn delete-btn" @click.stop="$emit('remove')" title="Supprimer">
          <i class="fas fa-trash"></i>
        </button>
      </div>
    </div>

    <!-- Corps du composant -->
    <div class="card">
      <div class="content">
        <!-- Section message -->
        <div class="message-section">
          <!-- Mode affichage -->
          <div v-if="!isEditingMessage" 
               class="message-display"
               @click="startEditMessage">
            <p v-if="element.message" 
               class="message-text"
               v-html="formatMessage(element.message)"></p>
            <p v-else 
               class="message-placeholder">Cliquez pour ajouter un message...</p>
            <span class="char-count" 
                  :class="{'char-count-limit': element.message?.length >= 640}">
              {{ element.message?.length || 0 }}/640
            </span>
          </div>

          <!-- Mode édition -->
          <div v-else class="message-editor">
            <div class="message-input-container">
              <textarea 
                ref="messageEditor"
                v-model="editedMessage"
                class="message-input"
                placeholder="Écrivez votre message ici..."
                @input="handleMessageInput"
                @keydown="handleMessageKeydown"
                @blur="handleBlur"
                maxlength="640"
              ></textarea>
              
              <!-- Bouton emoji -->
              <div class="emoji-button-container">
                <button type="button" class="emoji-button" @click.stop.prevent="toggleEmojiPicker">
                  <i class="far fa-smile"></i>
                </button>
                
                <!-- Sélecteur d'emoji -->
                <div v-if="showEmojiPicker" class="emoji-picker" @click.stop>
                  <div class="emoji-picker-content">
                    <div class="emoji-category">
                      <button 
                        v-for="emoji in commonEmojis" 
                        :key="emoji"
                        type="button"
                        class="emoji-btn" 
                        @mousedown.prevent="insertEmoji(emoji, $event)"
                      >
                        {{ emoji }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="message-footer">
              <span class="char-count" 
                    :class="{'char-count-limit': editedMessage.length >= 640}">
                {{ editedMessage.length }}/640
              </span>
            </div>
            
            <!-- Sélecteur de variables -->
            <div v-if="showVariableSuggestions" class="variable-suggestions" @click.stop>
              <div class="variable-suggestions-content">
                <div class="variable-category">
                  <button 
                    v-for="variable in filteredVariableSuggestions" 
                    :key="variable.id"
                    type="button"
                    class="variable-suggestion-item" 
                    @mousedown.prevent="insertVariable(variable, $event)"
                  >
                    {{ variable.id }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Section boutons -->
        <div class="buttons-section">
          <!-- Liste des boutons existants -->
          <div class="buttons-list">
            <template v-for="(button, index) in element.buttons || []" :key="index">
              <!-- Mode édition d'un bouton -->
              <div v-if="editingButtonIndex === index" class="button-edit-form">
                <div class="edit-form-header">
                  <h3>Éditer le bouton</h3>
                  <button class="close-edit-btn" @click="cancelEdit">
                    <i class="fas fa-times"></i>
                  </button>
                </div>

                <!-- Sélection du block -->
                <select 
                  v-model="selectedBlockId"
                  class="block-select"
                  @change="onBlockSelect">
                  <option value="" disabled>Sélectionner un block</option>
                  <option v-for="block in blocks" 
                          :key="block._id" 
                          :value="block._id">
                    {{ block.blockname || block.idblock }}
                  </option>
                </select>

                <!-- Titre du bouton -->
                <div class="input-group">
                  <input 
                    v-model="editButton.title"
                    type="text"
                    class="title-input"
                    placeholder="Titre du bouton"
                    maxlength="20"
                    @input="validateButtonTitle"
                  />
                  <span class="char-count" 
                        :class="{'char-count-limit': editButton.title?.length >= 20}">
                    {{ editButton.title?.length || 0 }}/20
                  </span>
                </div>

                <!-- Section payload -->
                <div v-if="editButton.hasOwnProperty('payload')" class="payload-section">
                  <div class="payload-header">
                    <span>Variables du payload</span>
                    <button class="remove-payload-btn" @click="removePayload" title="Supprimer le payload">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>

                  <!-- Liste des variables -->
                  <div v-if="payloadVariables.length > 0" class="payload-variables">
                    <div v-for="(variable, varIndex) in payloadVariables" 
                         :key="varIndex"
                         class="variable-item">
                      <span class="variable-content">
                        <span class="variable-name">{{variable.name}}</span>
                        <span class="variable-equals">=</span>
                        <span class="variable-value">{{variable.value}}</span>
                      </span>
                      <button class="delete-variable-btn" 
                              @click="deletePayloadVariable(variable)">
                        <i class="fas fa-times"></i>
                      </button>
                    </div>
                  </div>

                  <!-- Ajout de variable -->
                  <button class="add-variable-btn" 
                          @click="toggleVariableSelector"
                          v-if="!showVariableSelector">
                    <i class="fas fa-plus"></i>
                    {{ payloadVariables.length > 0 ? 'Ajouter une variable' : 'Ajouter votre première variable' }}
                  </button>

                  <!-- Sélecteur de variable -->
                  <div v-if="showVariableSelector" class="variable-selector">
                    <div class="variable-form">
                      <multiselect
                        v-model="selectedVariable"
                        :options="filteredVariables"
                        :searchable="true"
                        :close-on-select="true"
                        :show-labels="false"
                        placeholder="Sélectionner une variable"
                        label="id"
                        track-by="id"
                      >
                        <template #option="{ option }">
                          <span class="option-id">{{ option.id }}</span>
                          <span v-if="option.type" class="option-type">({{ option.type }})</span>
                        </template>
                      </multiselect>

                      <input 
                        v-model="variableValue"
                        type="text"
                        class="variable-value-input"
                        placeholder="Valeur"
                      />

                      <div class="variable-actions">
                        <button 
                          class="add-variable-confirm-btn"
                          @click="addVariableToPayload"
                          :disabled="!selectedVariable || !variableValue"
                          :title="isEditingExistingVariable ? 'Modifier la variable' : 'Ajouter la variable'">
                          <i class="fas fa-check"></i>
                        </button>
                        <button 
                          class="variable-cancel-btn"
                          @click="toggleVariableSelector"
                          title="Annuler">
                          <i class="fas fa-times"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Ajout initial de payload -->
                <button 
                  v-else
                  class="add-payload-btn"
                  @click="addPayload">
                  <i class="fas fa-code"></i>
                  Ajouter payload
                </button>

                <!-- Boutons d'action -->
                <div class="edit-actions">
                  <button 
                    class="save-btn"
                    @click="saveEditButton(index)"
                    :disabled="!editButton.title || showVariableSelector">
                    <i class="fas fa-check"></i>
                    Valider
                  </button>
                  <button class="cancel-btn" @click="cancelEdit">
                    <i class="fas fa-times"></i>
                    Annuler
                  </button>
                </div>
              </div>

              <!-- Affichage d'un bouton -->
              <div v-else class="button-display" :class="'button-width-' + (element.buttons?.length || 1)">
                <div class="button-wrapper">
                  <button 
                    class="notion-button"
                    @click.stop="startEditButton(index)">
                    <div class="button-content">
                      <span class="button-text">{{ button.title }}</span>
                      <div class="button-metadata">
                        <span class="block-name">{{ getBlockName(button.id) }}</span>
                        <span v-if="button.hasOwnProperty('payload')" 
                              class="payload-badge">
                          <i class="fas fa-code"></i>
                        </span>
                      </div>
                    </div>
                  </button>
                  <button 
                    class="delete-button"
                    @click.stop="handleDeleteButton(index)"
                    title="Supprimer ce bouton">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>
            </template>
          </div>

          <!-- Bouton d'ajout -->
          <button 
            v-if="(!element.buttons || element.buttons.length < 3)"
            class="add-button"
            @click="addButton">
            <i class="fas fa-plus-circle"></i>
            <span>Ajouter un bouton</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { axiosPeelo } from '@/utils/axios';
import Multiselect from 'vue-multiselect';

export default {
  name: 'TextButtonElement',
  components: {
    Multiselect
  },
  props: {
    element: {
      type: Object,
      required: true
    },
    variables: {
      type: Array,
      default: () => []
    },
    idBot: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isEditingMessage: false,
      editedMessage: '',
      editingButtonIndex: null,
      editButton: {},
      blocks: [],
      selectedVariable: null,
      variableValue: '',
      isEditingExistingVariable: false,
      currentVariableInPayload: null,
      payloadVariables: [],
      showVariableSelector: false,
      selectedBlockId: '',
      showDeleteConfirm: false,
      buttonToDelete: null,
      buttonIndexToDelete: null,
      showEmojiPicker: false,
      isInsertingEmoji: false,
      showVariableSuggestions: false,
      variableSuggestionQuery: '',
      cursorPosition: 0,
      commonEmojis: ['😀', '😁', '😂', '🤣', '😃', '😄', '😅', '😆', '😉', '😊', '😋', '😎', '😍', '😘', '🥰', '😗', '😙', '😚', '🙂', '🤗', '🤩', '🤔', '🤨', '😐', '😑', '😶', '🙄', '😏', '😣', '😥', '😮', '🤐', '😯', '😪', '😫', '😴', '😌', '😛', '😜', '😝', '🤤', '😒', '😓', '😔', '😕', '🙃', '🤑', '😲', '☹️', '🙁', '😖', '😞', '😟', '😤', '😢', '😭', '😦', '😧', '😨', '😩', '🤯', '😬', '😰', '😱', '🥵', '🥶', '😳', '🤪', '😵', '😡', '😠', '🤬', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '😇', '🥳', '🥴', '🥺', '🤠', '🤡', '🤥', '🤫', '🤭', '🧐', '🤓', '😈', '👿', '👹', '👺', '💀', '👻', '👽', '🤖', '💩', '👍', '👎', '👌', '✌️', '🤞', '🤘', '🤙', '👈', '👉', '☝️', '👆', '👇', '✋', '🤚', '🖐️', '🖖', '👋', '🤟', '✍️', '👏', '👐', '🙌', '🤲', '🙏', '🤝', '💪', '❤️', '💚', '💙', '💜', '🧡', '💛', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '🎉', '🔥', '✨', '🌟', '👏', '💯']
    };
  },
  computed: {
    filteredVariables() {
      return this.variables?.filter(v => v.type !== 'global') || [];
    },
    filteredVariableSuggestions() {
      // Pour les suggestions de message, on affiche toutes les variables (y compris globales)
      const allVariables = this.variables || [];
      
      if (!this.variableSuggestionQuery) {
        return allVariables;
      }
      
      return allVariables.filter(variable => 
        variable.id.toLowerCase().includes(this.variableSuggestionQuery.toLowerCase())
      );
    }
  },
  mounted() {
    this.fetchBlocks();
    this.ensureButtonExists();
    // Créer une version liée pour préserver le contexte this
    this.boundCloseEmojiPicker = this.closeEmojiPickerOnClickOutside.bind(this);
  },
  beforeDestroy() {
    // Nettoyer les écouteurs d'événements lors de la destruction du composant
    document.removeEventListener('click', this.boundCloseEmojiPicker);
  },
  methods: {
    // Gestion du message
    formatMessage(text) {
      if (!text) return '';
      
       
      // Remplacer les sauts de ligne par des <br>
      let formattedText = text.replace(/\n/g, '<br>');
      
      // Mettre en évidence les variables {{variable}} avec un style distinct
      formattedText = formattedText.replace(/{{([^}]+)}}/g, '<span class="variable-highlight">{{$1}}</span>');
      

      
      return formattedText;
    },
    startEditMessage() {
      this.isEditingMessage = true;
      this.editedMessage = this.element.message || '';
      this.$nextTick(() => {
        if (this.$refs.messageEditor) {
          this.$refs.messageEditor.focus();
          // Ajuster la hauteur initiale
          this.$refs.messageEditor.style.height = 'auto';
          this.$refs.messageEditor.style.height = Math.max(60, this.$refs.messageEditor.scrollHeight) + 'px';
        }
      });
    },
    handleMessageInput(event) {
      // Ajuster automatiquement la hauteur du textarea
      const textarea = event.target;
      textarea.style.height = 'auto';
      textarea.style.height = Math.max(60, textarea.scrollHeight) + 'px';
      
      // Mettre à jour la position du curseur
      this.cursorPosition = textarea.selectionStart;
      
      // Vérifier si l'utilisateur est en train de taper une variable
      const textBeforeCursor = this.editedMessage.substring(0, this.cursorPosition);
      const match = textBeforeCursor.match(/{{([^}]*)$/); // Cherche {{ suivi de caractères jusqu'au curseur
      
      if (match) {
        this.showVariableSuggestions = true;
        this.variableSuggestionQuery = match[1];
      } else {
        this.showVariableSuggestions = false;
      }
    },
    getBlockName(blockId) {
      if (!blockId) return '';
      const block = this.blocks.find(b => b.idblock === blockId);
      return block ? (block.blockname || block.idblock) : '';
    },
    handleMessageKeydown(event) {
      // Permettre les retours à la ligne avec Entrée
      if (event.key === 'Enter') {
        // Ne pas empêcher le comportement par défaut pour permettre le retour à la ligne
        return;
      }
      
      // Sauvegarder le message avec Ctrl+Enter ou Cmd+Enter
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        this.handleBlur();
      }
      
      // Fermer le sélecteur d'emojis avec Escape
      if (event.key === 'Escape') {
        if (this.showEmojiPicker) {
          this.showEmojiPicker = false;
          document.removeEventListener('click', this.boundCloseEmojiPicker);
        } else {
          // Si pas d'emoji picker ouvert, fermer l'édition
          this.handleBlur();
        }
      }
    },
    handleBlur() {
      // Ne pas traiter l'événement blur si nous sommes en train d'insérer un emoji
      if (this.isInsertingEmoji) {
        return;
      }
      
      // Utiliser un délai pour permettre aux événements de clic de se produire d'abord
      setTimeout(() => {
        // Vérifier si le clic est sur un élément lié aux emojis
        const isEmojiRelated = document.activeElement && (
          document.activeElement.closest('.emoji-button') ||
          document.activeElement.closest('.emoji-picker') ||
          document.activeElement.closest('.emoji-btn')
        );
        
        // Vérifier si le clic est sur un élément lié aux variables
        const isVariableRelated = document.activeElement && (
          document.activeElement.closest('.variable-suggestions') ||
          document.activeElement.closest('.variable-suggestion-item')
        );
        
        // Si le clic est sur un élément lié aux emojis ou variables, ne pas sauvegarder
        if (isEmojiRelated || isVariableRelated) {
          // Redonner le focus au textarea
          if (this.$refs.messageEditor) {
            this.$refs.messageEditor.focus();
          }
          return;
        }
        
        // Sinon, sauvegarder le message
        if (this.editedMessage !== this.element.message) {
          this.$emit('update', { ...this.element, message: this.editedMessage });
        }
        this.isEditingMessage = false;
        this.showVariableSuggestions = false;
      }, 100);
    },

    // Gestion des blocks
    async fetchBlocks() {
      try {
        const userData = localStorage.getItem('user');
        if (!userData) {
          this.blocks = [];
          return;
        }
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        const response = await axiosPeelo.get(
          `/strong/get/chatbot/${this.idBot}/flow`,
          config
        );
        if (response.data?.success && response.data.flow?.blocks) {
          this.blocks = response.data.flow.blocks;
        } else {
          this.blocks = [];
        }
      } catch (e) {
        console.error('Erreur lors de la récupération des blocks:', e);
        this.blocks = [];
      }
    },

    // Gestion des boutons
    ensureButtonExists() {
      if (!this.element.buttons || this.element.buttons.length === 0) {
        const updatedElement = {
          ...this.element,
          buttons: [{ title: '', id: '' }]
        };
        this.$emit('update', updatedElement);
      }
    },
    addButton() {
      const newButton = { title: '', id: '' };
      const updatedButtons = [...(this.element.buttons || []), newButton];
      this.$emit('update', { ...this.element, buttons: updatedButtons });
      this.startEditButton(updatedButtons.length - 1);
    },
    startEditButton(index) {
      this.editingButtonIndex = index;
      this.editButton = { ...this.element.buttons[index] };
      
      // Trouver le _id correspondant à l'idblock du bouton
      if (this.editButton.id) {
        const block = this.blocks.find(b => b.idblock === this.editButton.id);
        if (block) {
          this.selectedBlockId = block._id;
        }
      } else {
        this.selectedBlockId = '';
      }
      
      if (this.editButton.payload) {
        this.extractAllPayloadVariables(this.editButton.payload);
      } else {
        this.payloadVariables = [];
      }
    },
    validateButtonTitle() {
      if (this.editButton.title?.length > 20) {
        this.editButton.title = this.editButton.title.slice(0, 20);
      }
    },
    onBlockSelect() {
      const block = this.blocks.find(b => b._id === this.selectedBlockId);
      if (block) {
        this.editButton.id = block.idblock;
        if (block.blockname?.trim()) {
          this.editButton.title = block.blockname;
        }
      }
    },
    saveEditButton(index) {
      const updatedButtons = [...this.element.buttons];
      updatedButtons[index] = { ...this.editButton };
      this.$emit('update', { ...this.element, buttons: updatedButtons });
      this.cancelEdit();
    },
    cancelEdit() {
      this.editingButtonIndex = null;
      this.editButton = {};
      this.selectedVariable = null;
      this.variableValue = '';
      this.selectedBlockId = '';
      this.showVariableSelector = false;
    },
    handleDeleteButton(index) {
      this.buttonToDelete = { ...this.element.buttons[index] };
      this.buttonIndexToDelete = index;
      this.showDeleteConfirm = true;
    },
    cancelDelete() {
      this.showDeleteConfirm = false;
      this.buttonToDelete = null;
      this.buttonIndexToDelete = null;
    },
    confirmDelete() {
      const index = this.buttonIndexToDelete;
      const updatedButtons = [...this.element.buttons];
      updatedButtons.splice(index, 1);
      this.$emit('update', { ...this.element, buttons: updatedButtons });
      this.cancelDelete();
    },

    // Gestion du payload
    addPayload() {
      this.editButton.payload = '';
      this.showVariableSelector = true;
    },
    removePayload() {
      delete this.editButton.payload;
      this.payloadVariables = [];
      this.showVariableSelector = false;
    },
    toggleVariableSelector() {
      this.showVariableSelector = !this.showVariableSelector;
      this.selectedVariable = null;
      this.variableValue = '';
    },
    extractAllPayloadVariables(payload) {
      if (!payload) {
        this.payloadVariables = [];
        return;
      }
      
      const variables = [];
      const regex = /{{([^}]+)}}=([^&\s]+)/g;
      let match;
      
      while ((match = regex.exec(payload)) !== null) {
        const variableId = match[1];
        const value = match[2];
        const fullMatch = match[0];
        
        variables.push({
          id: variableId,
          name: variableId,
          value: value,
          fullMatch: fullMatch
        });
      }
      
      this.payloadVariables = variables;
    },
    addVariableToPayload() {
      if (!this.selectedVariable || !this.variableValue) return;
      
      const variableFormat = `{{${this.selectedVariable.id}}}=${this.variableValue}`;
      
      if (this.isEditingExistingVariable && this.currentVariableInPayload) {
        this.editButton.payload = this.editButton.payload.replace(
          this.currentVariableInPayload,
          variableFormat
        );
      } else {
        if (this.editButton.payload?.trim()) {
          this.editButton.payload += '&' + variableFormat;
        } else {
          this.editButton.payload = variableFormat;
        }
      }
      
      this.extractAllPayloadVariables(this.editButton.payload);
      this.selectedVariable = null;
      this.variableValue = '';
      this.isEditingExistingVariable = false;
      this.currentVariableInPayload = null;
      this.showVariableSelector = false;
    },
    deletePayloadVariable(variable) {
      if (!this.editButton.payload) return;
      
      this.editButton.payload = this.editButton.payload
        .replace(variable.fullMatch, '')
        .replace(/^&|&$/g, '')
        .replace(/&&/g, '&')
        .trim();
      
      this.extractAllPayloadVariables(this.editButton.payload);
      
      if (!this.editButton.payload) {
        delete this.editButton.payload;
      }
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
      
      if (this.showEmojiPicker) {
        this.$nextTick(() => {
          setTimeout(() => {
            document.addEventListener('click', this.boundCloseEmojiPicker);
          }, 0);
        });
      } else {
        document.removeEventListener('click', this.boundCloseEmojiPicker);
      }
      
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.messageEditor) {
            this.$refs.messageEditor.focus();
          }
        }, 10);
      });
    },
    insertEmoji(emoji, event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      this.isInsertingEmoji = true;
      
      const textarea = this.$refs.messageEditor;
      if (textarea) {
        const cursorPosition = textarea.selectionStart;
        const textBefore = this.editedMessage.substring(0, cursorPosition);
        const textAfter = this.editedMessage.substring(cursorPosition);
        
        this.editedMessage = textBefore + emoji + textAfter;
        
        // Ne pas fermer le picker pour permettre la sélection multiple
        // this.showEmojiPicker = false;
        // document.removeEventListener('click', this.boundCloseEmojiPicker);
        
        this.$nextTick(() => {
          setTimeout(() => {
            this.isInsertingEmoji = false;
            
            if (this.$refs.messageEditor) {
              this.$refs.messageEditor.focus();
              const newPosition = cursorPosition + emoji.length;
              this.$refs.messageEditor.selectionStart = newPosition;
              this.$refs.messageEditor.selectionEnd = newPosition;
              this.handleMessageInput({ target: textarea });
            }
          }, 50); // Réduit le délai pour une meilleure réactivité
        });
      }
    },
    closeEmojiPickerOnClickOutside(event) {
      if (!this.showEmojiPicker) return;
      
      if (event.target.closest('.emoji-picker') || 
          event.target.closest('.emoji-button') || 
          event.target.closest('.emoji')) {
        return;
      }
      
      this.showEmojiPicker = false;
      document.removeEventListener('click', this.boundCloseEmojiPicker);
      
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.messageEditor) {
            this.$refs.messageEditor.focus();
          }
        }, 10);
      });
    },
    insertVariable(variable, event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      this.isInsertingEmoji = true; // On utilise la même variable que pour les emojis
      const textBeforeCursor = this.editedMessage.substring(0, this.cursorPosition);
      const textAfterCursor = this.editedMessage.substring(this.cursorPosition);
      
      // Trouver où commence la variable en cours de saisie
      const match = textBeforeCursor.match(/{{([^}]*)$/); 
      
      if (match) {
        const startPos = textBeforeCursor.lastIndexOf('{{');
        const variableText = `{{${variable.id}}}`;
        
        // Remplacer le début de la variable par la variable complète
        this.editedMessage = textBeforeCursor.substring(0, startPos) + variableText + textAfterCursor;
        
        // Mettre à jour la position du curseur après la variable insérée
        this.$nextTick(() => {
          setTimeout(() => {
            this.isInsertingEmoji = false;
            
            if (this.$refs.messageEditor) {
              const newCursorPos = startPos + variableText.length;
              this.$refs.messageEditor.focus();
              this.$refs.messageEditor.selectionStart = newCursorPos;
              this.$refs.messageEditor.selectionEnd = newCursorPos;
              this.cursorPosition = newCursorPos;
              this.handleMessageInput({ target: this.$refs.messageEditor });
            }
          }, 50);
        });
      } else {
        // Si pas de variable en cours, insérer à la position du curseur
        const variableText = `{{${variable.id}}}`;
        this.editedMessage = textBeforeCursor + variableText + textAfterCursor;
        
        // Mettre à jour la position du curseur
        this.$nextTick(() => {
          setTimeout(() => {
            this.isInsertingEmoji = false;
            
            if (this.$refs.messageEditor) {
              const newCursorPos = this.cursorPosition + variableText.length;
              this.$refs.messageEditor.focus();
              this.$refs.messageEditor.selectionStart = newCursorPos;
              this.$refs.messageEditor.selectionEnd = newCursorPos;
              this.cursorPosition = newCursorPos;
              this.handleMessageInput({ target: this.$refs.messageEditor });
            }
          }, 50);
        });
      }
      
      this.showVariableSuggestions = false;
    }
  }
};
</script>

<style scoped>
/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-header {
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 500;
  color: #111827;
}

.modal-close {
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #f3f4f6;
  color: #374151;
}

.modal-body {
  padding: 16px;
}

.modal-body p {
  margin: 0 0 12px 0;
  color: #4b5563;
}

.button-to-delete {
  background: #f3f4f6;
  border-radius: 6px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.button-title {
  font-weight: 500;
  color: #111827;
}

.button-block {
  font-size: 0.85rem;
  color: #6b7280;
}

.modal-footer {
  padding: 16px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.modal-cancel,
.modal-confirm {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.modal-cancel {
  background: white;
  border: 1px solid #e5e7eb;
  color: #4b5563;
}

.modal-cancel:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.modal-confirm {
  background: #fee2e2;
  border: 1px solid #fecaca;
  color: #dc2626;
}

.modal-confirm:hover {
  background: #fecaca;
  border-color: #fca5a5;
}
</style>

<style scoped>
@import 'vue-multiselect/dist/vue-multiselect.css';

.text-button-element-wrapper {
  position: relative;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
}

/* Header styles */
.element-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.element-type-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-size: 0.875rem;
}

.element-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: white;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.action-btn.delete-btn:hover {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #ef4444;
}

/* Message section */
.message-section {
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.message-display {
  cursor: pointer;
  transition: all 0.2s ease;
}

.message-display:hover {
  background: rgba(59, 130, 246, 0.02);
  border-radius: 6px;
}

.message-text,
.message-placeholder {
  margin: 0;
  padding: 12px;
  min-height: 60px;
  height: auto;
  line-height: 1.5;
  color: #1f2937;
  font-family: inherit;
  font-size: 14px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  word-wrap: break-word;
  white-space: pre-wrap;
  display: block;
  box-sizing: border-box;
}

/* Style pour mettre en évidence les variables */
.text-button-element-wrapper .message-text .variable-highlight {
  background: #3b82f6 !important;
  color: white !important;
  padding: 4px 10px !important;
  border-radius: 16px !important;
  font-weight: 600 !important;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
  font-size: 0.8em !important;
  display: inline-block !important;
  margin: 0 3px 2px 0 !important;
  vertical-align: middle !important;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3) !important;
  border: none !important;
  white-space: nowrap !important;
  text-shadow: none !important;
  position: relative !important;
}

.text-button-element-wrapper .message-text .variable-highlight::before {
  content: '●' !important;
  position: absolute !important;
  left: 4px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  color: white !important;
  font-size: 0.6em !important;
  opacity: 0.8 !important;
}

.text-button-element-wrapper .message-text .variable-highlight:hover {
  background: #1d4ed8 !important;
  transform: translateY(-1px) scale(1.05) !important;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4) !important;
}

.text-button-element-wrapper .message-text .variable-highlight:active {
  transform: translateY(0) scale(1.02) !important;
}

.message-placeholder {
  color: #9ca3af;
  cursor: pointer;
  font-style: italic;
}

.message-editor {
  position: relative;
}

.message-input-container {
  position: relative;
  display: flex;
  align-items: flex-start;
}

.message-input {
  width: 100%;
  min-height: 60px;
  height: auto;
  padding: 12px 50px 12px 12px;
  border: 1px solid transparent;
  border-radius: 6px;
  resize: none;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  color: #1f2937;
  background: transparent;
  outline: none;
  transition: all 0.2s ease;
  word-wrap: break-word;
  white-space: pre-wrap;
  overflow: hidden;
  display: block;
  box-sizing: border-box;
}

.message-input:focus {
  border-color: transparent;
  box-shadow: none;
}

.emoji-button-container {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
}

.emoji-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
}

.emoji-button:hover {
  background: #f3f4f6;
  color: #3b82f6;
  transform: scale(1.1);
}

.emoji-picker {
  position: absolute;
  top: 100%;
  right: 0;
  width: 300px;
  max-height: 250px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  margin-top: 8px;
  animation: fadeIn 0.2s ease;
  overflow-y: auto;
  transform-origin: top right;
  border: 1px solid #e5e7eb;
}

.emoji-picker-content {
  padding: 12px;
}

.emoji-category {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.emoji-btn {
  cursor: pointer;
  font-size: 18px;
  padding: 6px;
  border-radius: 6px;
  transition: all 0.2s ease;
  background: none;
  border: none;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
}

.emoji-btn:hover {
  background: #f3f4f6;
  transform: scale(1.1);
  border-radius: 8px;
}

.message-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 8px 0;
}

.char-count {
  display: block;
  text-align: right;
  font-size: 0.75rem;
  color: #6b7280;
  margin-left: 4px;
}

.char-count-limit {
  color: #ef4444;
  font-weight: 500;
}

@keyframes fadeIn {
  from { 
    opacity: 0; 
    transform: translateY(-10px) scale(0.95); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0) scale(1); 
  }
}

/* Buttons section */
.buttons-section {
  padding: 16px;
}

.buttons-list {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  margin-bottom: 16px;
  width: 100%;
  min-height: 76px;
  position: relative;
}

.button-display {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  position: relative;
}

.button-width-1 {
  width: 100%;
}

.button-width-2 {
  width: calc(50% - 4px);
}

.button-width-3 {
  width: calc(33.333% - 5.333px);
}

.button-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 76px;
  display: flex;
}

.notion-button {
  background: #e6f7f1;
  border: 1px solid #dcf2ea;
  border-radius: 6px;
  padding: 12px 16px;
  color: #0f766e;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  transition: all 0.2s;
  width: 100%;
  height: 100%;
  font-size: 0.95rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.notion-button:hover {
  background: #dcf2ea;
  border-color: #bae6dc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.button-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.button-metadata {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.block-name {
  font-size: 0.75rem;
  color: #64748b;
  opacity: 0.9;
}

.whatsapp-button:hover {
  background: #d1d5db;
}

.button-text {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.payload-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #e0f2fe;
  color: #0284c7;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
}

.delete-button {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  border-radius: 3px;
  border: none;
  background: transparent;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  opacity: 0;
  padding: 0;
  font-size: 0.75rem;
  z-index: 2;
}

.button-wrapper:hover .delete-button {
  opacity: 1;
}

.delete-button:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.add-button {
  width: 100%;
  padding: 8px;
  background: #f3f4f6;
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
}

.add-button:hover {
  background: #e5e7eb;
  border-color: #9ca3af;
  color: #4b5563;
}

/* Remove payload button */
.remove-payload-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #9ca3af;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
}

.remove-payload-btn:hover {
  background: #fee2e2;
  color: #dc2626;
}

/* Add payload button */
.add-payload-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
  color: #6b7280;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 12px;
}

.add-payload-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
  color: #374151;
}

.add-payload-btn i {
  font-size: 0.85rem;
}

.add-variable-confirm-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: #10b981;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;
}

.add-variable-confirm-btn:hover:not(:disabled) {
  background: #059669;
  transform: scale(1.05);
}

.add-variable-confirm-btn:disabled {
  background: #d1fae5;
  color: #6b7280;
  cursor: not-allowed;
}

.variable-actions {
  display: flex;
  gap: 8px;
}

.variable-cancel-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: #fee2e2;
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;
}

.variable-cancel-btn:hover {
  background: #fecaca;
  transform: scale(1.05);
}

/* Button edit form */
.button-edit-form {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  width: 100%;
  max-height: none;
  z-index: 999;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 8px;
}

/* Style de la scrollbar pour Webkit (Chrome, Safari, etc.) */
.button-edit-form::-webkit-scrollbar {
  width: 8px;
}

.button-edit-form::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.button-edit-form::-webkit-scrollbar-thumb {
  background: #94a3b8;
  border-radius: 4px;
}

.button-edit-form::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}

.edit-form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  position: sticky;
  top: 0;
  background: white;
  padding: 8px 0;
  z-index: 1;
}

.close-edit-btn {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: none;
  background: #f3f4f6;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.block-select,
.title-input {
  width: 100%;
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  margin-bottom: 12px;
}

/* Payload section */
.payload-section {
  margin-top: 16px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 6px;
}

.payload-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.payload-variables {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.variable-item {
  display: flex;
  align-items: center;
  gap: 4px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 4px 8px;
}

.variable-content {
  display: flex;
  align-items: center;
  gap: 4px;
}

.variable-name {
  color: #0284c7;
}

.variable-equals {
  color: #6b7280;
}

.variable-value {
  color: #1f2937;
}

.delete-variable-btn {
  width: 20px;
  height: 20px;
  border: none;
  background: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-variable-btn:hover {
  color: #ef4444;
}

/* Variable selector */
.add-variable-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 8px 12px;
  border: 1px dashed #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
  color: #6b7280;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 8px;
}

.add-variable-btn:hover {
  background: #f3f4f6;
  border-color: #0ea5e9;
  color: #0ea5e9;
}

.add-variable-btn i {
  font-size: 0.85rem;
}

.variable-selector {
  margin-top: 12px;
}

.variable-form {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.variable-value-input {
  flex: 1;
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

/* Buttons */
.edit-actions {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}

.save-btn,
.cancel-btn {
  flex: 1;
  padding: 8px 16px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.save-btn {
  background: #10b981;
  border: none;
  color: white;
}

.save-btn:hover:not(:disabled) {
  background: #059669;
}

.save-btn:disabled {
  background: #d1fae5;
  cursor: not-allowed;
}

.cancel-btn {
  background: white;
  border: 1px solid #e5e7eb;
  color: #6b7280;
}

.cancel-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

/* Vue Multiselect customization */
.multiselect {
  min-height: 38px;
}

.multiselect__tags {
  min-height: 38px;
  padding: 5px 8px;
  border-color: #e5e7eb;
  border-radius: 6px;
}

.multiselect__tag {
  background: #e0f2fe;
  color: #0284c7;
}

.multiselect__option--highlight {
  background: #0284c7;
}

.variable-suggestions {
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  margin-bottom: 8px;
  animation: fadeIn 0.2s ease;
  border: 1px solid #e5e7eb;
  max-height: 200px;
  overflow-y: auto;
}

.variable-suggestions-content {
  padding: 12px;
}

.variable-category {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.variable-suggestion-item {
  padding: 6px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #f0f9ff;
  border: 1px solid #0ea5e9;
  outline: none;
  border-radius: 16px;
  font-size: 0.875rem;
  color: #0c4a6e;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(14, 165, 233, 0.1);
}

.variable-suggestion-item:hover {
  background: #0ea5e9;
  color: white;
  border-color: #0284c7;
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.variable-suggestion-item:focus {
  background: #0284c7;
  color: white;
  border-color: #0369a1;
  outline: 2px solid #38bdf8;
  outline-offset: 2px;
}
</style>